import React, { forwardRef, memo, useCallback, useMemo } from "react";
import PropTypes from "prop-types";
import { Avatar, AvatarBadge, Button, Box, Icon } from "@chakra-ui/react";
import {
  FaBuilding,
  FaCalendarDay,
  FaChalkboard,
  FaExclamationCircle,
  FaStore,
  FaUser,
  FaUsers,
  FaVideo,
} from "react-icons/fa";
import { getSocialProviderBySocialType } from "../../../_utils/socialProvider";
import SocialTypeIcon from "../SocialTypeIcon/SocialTypeIcon";
import * as SocialTypes from "../../../_constants/SocialType";
import * as SocialProviders from "../../../_constants/SocialProvider";

const getColorByType = (type) => {
  const provider = getSocialProviderBySocialType(type);
  let foreground = "gray.300";
  let background = "gray.100";

  if (provider === SocialProviders.FACEBOOK) {
    foreground = "blue.100";
    background = "blue.50";
  } else if (provider === SocialProviders.TWITTER) {
    foreground = "cyan.200";
    background = "cyan.50";
  } else if (provider === SocialProviders.GOOGLE) {
    foreground = "red.100";
    background = "red.50";
  } else if (provider === SocialProviders.YOUTUBE) {
    foreground = "red.100";
    background = "red.50";
  } else if (provider === SocialProviders.LINKEDIN) {
    foreground = "gray.300";
    background = "gray.100";
  } else if (provider === SocialProviders.VKONTAKTE) {
    foreground = "blue.100";
    background = "blue.50";
  } else if (provider === SocialProviders.INSTAGRAM) {
    foreground = "pink.100";
    background = "pink.50";
  } else if (provider === SocialProviders.PINTEREST) {
    foreground = "red.100";
    background = "red.50";
  } else if (provider === SocialProviders.TIKTOK) {
    foreground = "white";
    background = "black";
  }

  return {
    foreground,
    background,
  };
};

const getIconByType = (type) => {
  let icon = FaUser;

  switch (type) {
    case SocialTypes.FACEBOOK_PAGE:
    case SocialTypes.LINKEDIN_COMPANY:
    case SocialTypes.GOOGLE_PAGE:
    case SocialTypes.VKONTAKTE_PAGE:
      icon = FaBuilding;
      break;
    case SocialTypes.GOOGLE_GMB:
      icon = FaStore;
      break;
    case SocialTypes.YOUTUBE_CHANNEL:
      icon = FaVideo;
      break;
    case SocialTypes.FACEBOOK_GROUP:
    case SocialTypes.LINKEDIN_GROUP:
    case SocialTypes.VKONTAKTE_GROUP:
      icon = FaUsers;
      break;
    case SocialTypes.VKONTAKTE_EVENT:
      icon = FaCalendarDay;
      break;
    case SocialTypes.PINTEREST_BOARD:
      icon = FaChalkboard;
      break;
    default:
      break;
  }

  return icon;
};

const sizes = {
  xs: "22px",
  sm: "32px",
  md: "40px",
  lg: "50px",
};

const badgeSizes = {
  xs: "12px",
  sm: "12px",
  md: "14px",
  lg: "18px",
};

const accountStyles = {
  "> img": {
    width: "full",
    height: "full",
    borderRadius: "inherit",
  },
};

const SocialAccountAvatar = forwardRef(
  (
    {
      children,
      hasError,
      name,
      src,
      socialType,
      size,
      sx,
      onClick,
      selected,
      selectable,
      socialTypeShown,
      ...props
    },
    ref
  ) => {
    const getInitials = useCallback(() => null, []);
    const colors = useMemo(() => getColorByType(socialType), [socialType]);
    const icon = useMemo(() => getIconByType(socialType), [socialType]);

    return (
      <Avatar
        as={onClick ? Button : Box}
        ref={ref}
        aria-label={name}
        src={src}
        getInitials={getInitials}
        color={colors.foreground}
        backgroundColor={colors.background}
        onClick={onClick}
        opacity={selected || !selectable ? "1" : "0.5"}
        icon={<Icon width="60%" height="60%" as={icon} />}
        padding="0"
        width={sizes[size]}
        height={sizes[size]}
        borderStyle="solid"
        borderWidth={selectable ? "2px" : "0px"}
        borderColor={selected ? "primary.400" : "gray.200"}
        sx={{
          ...accountStyles,
          ...sx,
        }}
        {...props}
      >
        {children}
        {socialTypeShown && (
          <AvatarBadge
            sx={{
              borderWidth: "2px",
              borderColor: "white",
              borderStyle: "solid",
              fontSize: badgeSizes[size],
            }}
          >
            <SocialTypeIcon socialType={socialType} />
          </AvatarBadge>
        )}
        {hasError && (
          <Icon
            as={FaExclamationCircle}
            color="brand.negative"
            fontSize="16px"
            sx={{
              bg: "white",
              borderRadius: "full",
              position: "absolute",
              borderWidth: "2px",
              padding: "2px",
              left: "-2px",
              bottom: "-4px",
            }}
          />
        )}
      </Avatar>
    );
  }
);

SocialAccountAvatar.propTypes = {
  name: PropTypes.string,
  hasError: PropTypes.bool,
  onClick: PropTypes.func,
  selected: PropTypes.bool,
  selectable: PropTypes.bool,
  socialTypeShown: PropTypes.bool,
  src: PropTypes.string,
  socialType: PropTypes.string,
  sx: PropTypes.object,
  size: PropTypes.oneOf(["xs", "sm", "md", "lg"]),
};

SocialAccountAvatar.defaultProps = {
  name: null,
  hasError: false,
  onClick: undefined,
  selected: false,
  selectable: false,
  socialTypeShown: false,
  src: null,
  socialType: null,
  sx: {},
  size: "md",
};

export default memo(SocialAccountAvatar);
